import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTenantLocation, useMoney } from '../context/LocationContext';
import { api } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { RefreshCw, Search, Loader2, ShoppingCart, Banknote, Receipt, Download } from 'lucide-react';
import { cn } from '../lib/utils';

export default function Sales() {
  const { token } = useAuth();
  const { currentLocationId } = useTenantLocation();
  const { money, totals } = useMoney();
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [pullingFromUntill, setPullingFromUntill] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [syncStatusFilter, setSyncStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const fetchSales = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (syncStatusFilter !== 'all') params.sync_status = syncStatusFilter;
      const response = await api.getSales(token, params);
      setSales(response.data);
    } catch (error) {
      console.error('Failed to fetch sales:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, statusFilter, syncStatusFilter, currentLocationId]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await api.syncSales(token);
      toast.success(result.data.message);
      fetchSales();
    } catch (error) {
      toast.error('Sales sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handlePullFromUntill = async () => {
    setPullingFromUntill(true);
    try {
      const params = {};
      if (currentLocationId) params.location_id = currentLocationId;
      const r = await api.syncSalesFromUntill(token, params);
      const { fetched, inserted, updated, endpoint_used } = r.data;
      toast.success(
        `Pulled ${fetched} bills from unTill (${inserted} new, ${updated} updated) via /${endpoint_used}`
      );
      fetchSales();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'unTill sales pull failed');
    } finally {
      setPullingFromUntill(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      completed: 'status-success',
      refunded: 'status-error',
      voided: 'status-error',
      pending: 'status-pending'
    };
    return styles[status] || 'status-pending';
  };

  const getSyncStatusBadge = (status) => {
    const styles = {
      synced: 'status-success',
      failed: 'status-error',
      pending: 'status-pending'
    };
    return styles[status] || 'status-pending';
  };

  const filteredSales = sales.filter(sale => 
    sale.receipt_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    sale.store_id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalRevenue = totals(filteredSales.filter(s => s.status === 'completed'), (s) => s.total);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="sales-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-heading text-white tracking-tight">
            Sales Transactions
          </h1>
          <p className="text-slate-400 mt-1">Manage and sync sales from UnTill to Odoo</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={handlePullFromUntill}
            disabled={pullingFromUntill}
            variant="outline"
            className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 hover:text-white"
            data-testid="pull-untill-sales-btn"
          >
            {pullingFromUntill ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
            Pull from unTill
          </Button>
          <Button
            onClick={handleSync}
            disabled={syncing}
            className="bg-blue-500 hover:bg-blue-600 text-white"
            data-testid="sync-sales-btn"
          >
            {syncing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Sync Sales
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-6 flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Total Transactions</p>
              <p className="text-2xl font-bold text-white mt-1">{sales.length}</p>
            </div>
            <Receipt className="w-8 h-8 text-blue-400" />
          </CardContent>
        </Card>
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-6 flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Total Revenue</p>
              <p className="text-2xl font-bold text-emerald-400 mt-1" data-testid="sales-total-revenue">{totalRevenue}</p>
            </div>
            <Banknote className="w-8 h-8 text-emerald-400" />
          </CardContent>
        </Card>
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-6 flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Pending Sync</p>
              <p className="text-2xl font-bold text-amber-400 mt-1">
                {sales.filter(s => s.sync_status === 'pending').length}
              </p>
            </div>
            <ShoppingCart className="w-8 h-8 text-amber-400" />
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                placeholder="Search by receipt number or store..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-slate-800/50 border-slate-700 text-white"
                data-testid="sales-search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px] bg-slate-800/50 border-slate-700 text-white" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800">
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="refunded">Refunded</SelectItem>
                <SelectItem value="voided">Voided</SelectItem>
              </SelectContent>
            </Select>
            <Select value={syncStatusFilter} onValueChange={setSyncStatusFilter}>
              <SelectTrigger className="w-[180px] bg-slate-800/50 border-slate-700 text-white" data-testid="sync-status-filter">
                <SelectValue placeholder="Sync Status" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800">
                <SelectItem value="all">All Sync Status</SelectItem>
                <SelectItem value="synced">Synced</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Sales Table */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
            </div>
          ) : filteredSales.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              No sales found. Seed demo data from the dashboard.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-800 hover:bg-transparent">
                  <TableHead className="text-slate-400">Receipt #</TableHead>
                  <TableHead className="text-slate-400">Store / Terminal</TableHead>
                  <TableHead className="text-slate-400">Items</TableHead>
                  <TableHead className="text-slate-400">Total</TableHead>
                  <TableHead className="text-slate-400">Payment</TableHead>
                  <TableHead className="text-slate-400">Status</TableHead>
                  <TableHead className="text-slate-400">Sync Status</TableHead>
                  <TableHead className="text-slate-400">Odoo Invoice</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSales.map((sale) => (
                  <TableRow key={sale.id} className="border-slate-800 data-table-row">
                    <TableCell className="font-mono text-white">{sale.receipt_number}</TableCell>
                    <TableCell className="text-slate-300">
                      {sale.store_id} / {sale.terminal_id}
                    </TableCell>
                    <TableCell className="text-slate-300">{sale.items.length} items</TableCell>
                    <TableCell className="text-white font-medium">{money(sale.total, sale.location_id)}</TableCell>
                    <TableCell className="text-slate-300">{sale.payment_method}</TableCell>
                    <TableCell>
                      <Badge className={getStatusBadge(sale.status)}>{sale.status}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={getSyncStatusBadge(sale.sync_status)}>{sale.sync_status}</Badge>
                    </TableCell>
                    <TableCell className="font-mono text-slate-400 text-sm">
                      {sale.odoo_invoice_id || '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
